import './index.less';

import React, { useCallback, useEffect, useState } from 'react';

import { STATIC_FILE_URL } from '@/shared/constants';
import { useWindowSize } from 'react-use';

const CpuArchitecture = () => {
  const { height, width } = useWindowSize();
  const [translateCenterYValue, setTranslateCenterYValue] = useState<number>(0);
  const [scaleValue, setScaleValue] = useState<number>(1);

  const translateCenterY = useCallback(() => {
    if (height < 800) {
      setTranslateCenterYValue(80);
      return;
    }

    if (height < 900) {
      setTranslateCenterYValue(60);
      return;
    }

    if (height < 1000) {
      setTranslateCenterYValue(50);
      return;
    }

    if (height < 1200) {
      setTranslateCenterYValue(25);
      return;
    }

    setTranslateCenterYValue(0);
    return;
  }, [height]);

  const scaleViz = useCallback(() => {
    if (width < 600) {
      setScaleValue(0.9);
      return;
    }

    if (height < 700) {
      setScaleValue(0.8);
      return;
    }

    if (height < 800) {
      setScaleValue(0.9);
      return;
    }

    setScaleValue(1);
    return;
  }, [height, width]);

  useEffect(() => {
    if (height > 0 || width > 0) {
      scaleViz();
      translateCenterY();
    }
  }, [height, width, scaleViz, translateCenterY]);

  const TOOLS = [
    { name: 'Google Analytics', path: `${STATIC_FILE_URL}/39f367367da5c7c7a40822e9d2045ff4.svg` },
    { name: 'Google Ads', path: `${STATIC_FILE_URL}/1188ff7ecc582b7283f676ea6ececd7f.webp` },
    { name: 'Google Drive', path: `${STATIC_FILE_URL}/344bec2afc896b7fcf9be7253befc05b.png` },
    { name: 'OneDrive', path: `${STATIC_FILE_URL}/e569c63904dae253f33ba7955b34fee6.svg` },
    { name: 'Klaviyo', path: `${STATIC_FILE_URL}/3db82702bb2653ba04a6efabd72c6a96.svg` },
    { name: 'Slack', path: `${STATIC_FILE_URL}/0c1219ff9b42993db203499d9ae41f8d.png` },
    { name: 'Meta Ads', path: `${STATIC_FILE_URL}/146489958fdd8bbfba1512f9421b06c0.svg` },
    { name: 'Shopify', path: `${STATIC_FILE_URL}/4da4ea48fbce7084c22e8fdca7668411.svg` },
    { name: 'Instagram', path: `${STATIC_FILE_URL}/fbddaa899b054fc69cba2fc826707e77.svg` },
    { name: 'TikTok', path: `${STATIC_FILE_URL}/c5ac48af64483d128907b31f39602a68.svg` },
    { name: 'Word', path: `${STATIC_FILE_URL}/372423df5299ffc5ab09fc537638baa6.png` },
    { name: 'Adobe Acrobat', path: `${STATIC_FILE_URL}/f4cca6162680479ad7e51f7fd967ea42.png` },
    { name: 'Excel', path: `${STATIC_FILE_URL}/a962cc5a0e19069621a9f90b18d3ad5e.png` },
  ];

  // Chip geometry (must match the constants below)
  const _ICX = 600,
    _ICY = 350 + translateCenterYValue,
    _ICW = 80,
    _ICH = 56;

  const _IL = _ICX - _ICW / 2,
    _IR = _ICX + _ICW / 2;

  const _IT = _ICY - _ICH / 2,
    _IB = _ICY + _ICH / 2;

  const _IPIN = 8;
  const ICON_R = 12 * scaleValue; // icon badge radius
  const ELLIPSE_RX = 135.85 * scaleValue,
    ELLIPSE_RY = 107.25 * scaleValue;

  // Place icons evenly around an ellipse, offset so none sit at 0/180 (left/right conflict)
  const ICON_POSITIONS = TOOLS.map((tool, i) => {
    const angle = (2 * Math.PI * i) / TOOLS.length - Math.PI / 2;
    const ix = _ICX + ELLIPSE_RX * Math.cos(angle);
    const iy = _ICY + ELLIPSE_RY * Math.sin(angle);
    // Determine nearest chip side and route accordingly
    const dx = ix - _ICX,
      dy = iy - _ICY;
    let pinX: number, pinY: number, traceD: string;
    if (Math.abs(dx) / (_ICW / 2) > Math.abs(dy) / (_ICH / 2)) {
      // left or right side
      if (dx > 0) {
        pinX = _IR + _IPIN;
        pinY = _ICY + dy * 0.35;
        traceD = `M ${ix} ${iy} H ${_IR + 40} V ${pinY} H ${pinX}`;
      } else {
        pinX = _IL - _IPIN;
        pinY = _ICY + dy * 0.35;
        traceD = `M ${ix} ${iy} H ${_IL - 40} V ${pinY} H ${pinX}`;
      }
    } else {
      // top or bottom side
      if (dy > 0) {
        pinX = _ICX + dx * 0.35;
        pinY = _IB + _IPIN;
        traceD = `M ${ix} ${iy} V ${_IB + 40} H ${pinX} V ${pinY}`;
      } else {
        pinX = _ICX + dx * 0.35;
        pinY = _IT - _IPIN;
        traceD = `M ${ix} ${iy} V ${_IT - 40} H ${pinX} V ${pinY}`;
      }
    }
    return { ...tool, ix, iy, traceD, idx: i };
  });

  const ICON_GRAD_COLORS = [
    '#ff5c3c',
    '#b0b8c8',
    '#7c4dff',
    '#a8b0c0',
    '#ff5c3c',
    '#00b8d4',
    '#b8bcc8',
    '#ff8a65',
    '#c0c8d4',
    '#ff5c3c',
    '#7c4dff',
    '#b0b4c4',
    '#00b8d4',
  ];

  // ViewBox: 1200 x 700. Chip center: (600, 350). Chip body: 80w x 56h.
  // CX=600 CY=350 CW=80 CH=56 => left=560 right=640 top=322 bottom=378
  // 16 traces fan to all 4 edges.

  const CX = 600,
    CY = 350 + translateCenterYValue,
    CW = 80,
    CH = 56;
  const L = CX - CW / 2,
    R = CX + CW / 2,
    T = CY - CH / 2,
    B = CY + CH / 2;
  const PIN = 8; // pin stub length

  // Each trace: path d (from edge → chip pin), dur, begin, gradId
  const TRACES: { d: string; dur: string; begin: string; grad: string }[] = [
    // TOP edge (6) → top pin stubs
    { d: `M 80 0 V ${T - 60} H ${L + 16} V ${T - PIN}`, dur: '5.76s', begin: '0s', grad: 'g0' },
    { d: `M 280 0 V ${T - 40} H ${L + 30} V ${T - PIN}`, dur: '5.04s', begin: '0.3s', grad: 'g1' },
    { d: `M 460 0 V ${T - 20} H ${L + 16} V ${T - PIN}`, dur: '6.3s', begin: '0.9s', grad: 'g2' },
    { d: `M 740 0 V ${T - 20} H ${R - 16} V ${T - PIN}`, dur: '5.4s', begin: '0.7s', grad: 'g3' },
    { d: `M 920 0 V ${T - 40} H ${R - 30} V ${T - PIN}`, dur: '4.5s', begin: '1.1s', grad: 'g4' },
    { d: `M 1100 0 V ${T - 60} H ${R - 16} V ${T - PIN}`, dur: '5.22s', begin: '0.5s', grad: 'g5' },
    // BOTTOM edge (6) → bottom pin stubs
    { d: `M 100 700 V ${B + 60} H ${L + 16} V ${B + PIN}`, dur: '5.22s', begin: '0.2s', grad: 'g6' },
    { d: `M 300 700 V ${B + 40} H ${L + 30} V ${B + PIN}`, dur: '6.12s', begin: '0.6s', grad: 'g7' },
    { d: `M 480 700 V ${B + 20} H ${L + 16} V ${B + PIN}`, dur: '5.58s', begin: '1.3s', grad: 'g8' },
    { d: `M 720 700 V ${B + 20} H ${R - 16} V ${B + PIN}`, dur: '4.86s', begin: '1.0s', grad: 'g9' },
    { d: `M 900 700 V ${B + 40} H ${R - 30} V ${B + PIN}`, dur: '6.48s', begin: '0.4s', grad: 'g10' },
    { d: `M 1100 700 V ${B + 60} H ${R - 16} V ${B + PIN}`, dur: '5.58s', begin: '1.4s', grad: 'g11' },
    // LEFT edge (6) → left pin stubs
    { d: `M 0 80  H ${L - 60} V ${T + 12} H ${L - PIN}`, dur: '4.68s', begin: '0.4s', grad: 'g12' },
    { d: `M 0 200 H ${L - 40} V ${T + 26} H ${L - PIN}`, dur: '5.94s', begin: '0.9s', grad: 'g13' },
    { d: `M 0 320 H ${L - 20} V ${T + 12} H ${L - PIN}`, dur: '4.32s', begin: '0.1s', grad: 'g14' },
    { d: `M 0 380 H ${L - 20} V ${B - 12} H ${L - PIN}`, dur: '5.4s', begin: '1.5s', grad: 'g15' },
    { d: `M 0 500 H ${L - 40} V ${B - 26} H ${L - PIN}`, dur: '6.48s', begin: '0.8s', grad: 'g16' },
    { d: `M 0 620 H ${L - 60} V ${B - 12} H ${L - PIN}`, dur: '4.86s', begin: '0.2s', grad: 'g17' },
    // RIGHT edge (6) → right pin stubs
    { d: `M 1200 80  H ${R + 60} V ${T + 12} H ${R + PIN}`, dur: '5.04s', begin: '0.5s', grad: 'g18' },
    { d: `M 1200 200 H ${R + 40} V ${T + 26} H ${R + PIN}`, dur: '5.76s', begin: '1.2s', grad: 'g19' },
    { d: `M 1200 320 H ${R + 20} V ${T + 12} H ${R + PIN}`, dur: '4.68s', begin: '0.3s', grad: 'g20' },
    { d: `M 1200 380 H ${R + 20} V ${B - 12} H ${R + PIN}`, dur: '6.12s', begin: '1.7s', grad: 'g21' },
    { d: `M 1200 500 H ${R + 40} V ${B - 26} H ${R + PIN}`, dur: '6.3s', begin: '1.6s', grad: 'g22' },
    { d: `M 1200 620 H ${R + 60} V ${B - 12} H ${R + PIN}`, dur: '5.22s', begin: '0.6s', grad: 'g23' },
  ];

  const GRAD_COLORS = [
    '#ff5c3c',
    '#b0b8c8',
    '#c9c9e1',
    '#a8b0c0',
    '#ff5c3c',
    '#b8bcc8',
    '#c0c8d4',
    '#d0d4e0',
    '#8e97a7',
    '#ff5c3c',
    '#c4c8d8',
    '#b0b4c4',
    '#d4d8e4',
    '#ff5c3c',
    '#a8acbc',
    '#bcc0d0',
    '#c8ccd8',
    '#ff5c3c',
    '#b4b8c8',
    '#d0d8e4',
    '#ff5c3c',
    '#a4acbc',
    '#c8d0dc',
    '#b8bcc8',
  ];

  if (width === 0 || height === 0) {
    return <div></div>;
  }

  return (
    <svg className="hero-viz" id="pietra-circuit" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice">
      <defs>
        {/* Chip body gradient */}
        <linearGradient id="chip-body-filter" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#333333" />
          <stop offset="50%" stopColor="#222222" />
          <stop offset="100%" stopColor="#111111" />
        </linearGradient>
        {/* Drop shadow for chip */}
        <filter id="chip-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#9090b0" floodOpacity="0.12" />
        </filter>
        {/* Per-trace radial glow gradients */}
        {GRAD_COLORS.map((c, i) => (
          <linearGradient key={i} id={`g${i}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={c} stopOpacity="0" />
            <stop offset="40%" stopColor={c} stopOpacity="0.9" />
            <stop offset="60%" stopColor={c} stopOpacity="1" />
            <stop offset="100%" stopColor={c} stopOpacity="0" />
          </linearGradient>
        ))}
        {/* Masks — one per trace, matching path geometry */}
        {TRACES.map((tr, i) => (
          <mask key={i} id={`mask${i}`}>
            <path d={tr.d} stroke="white" strokeWidth="1" fill="none" />
          </mask>
        ))}
        {/* Named paths for animateMotion */}
        {TRACES.map((tr, i) => (
          <path key={i} id={`tp${i}`} d={tr.d} />
        ))}
        {/* Icon trace gradients */}
        {ICON_GRAD_COLORS.map((c, i) => (
          <linearGradient key={`ig${i}`} id={`ig${i}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={c} stopOpacity="0" />
            <stop offset="40%" stopColor={c} stopOpacity="0.9" />
            <stop offset="60%" stopColor={c} stopOpacity="1" />
            <stop offset="100%" stopColor={c} stopOpacity="0" />
          </linearGradient>
        ))}
        {/* Icon trace masks */}
        {ICON_POSITIONS.map((ip) => (
          <mask key={`im${ip.idx}`} id={`im${ip.idx}`}>
            <path d={ip.traceD} stroke="white" strokeWidth="1" fill="none" />
          </mask>
        ))}
        {/* Icon trace named paths for animateMotion */}
        {ICON_POSITIONS.map((ip) => (
          <path key={`itp${ip.idx}`} id={`itp${ip.idx}`} d={ip.traceD} />
        ))}
        {/* Icon badge clip paths */}
        {ICON_POSITIONS.map((ip) => (
          <clipPath key={`icp${ip.idx}`} id={`icp${ip.idx}`}>
            <circle cx={ip.ix} cy={ip.iy} r={ICON_R - 3} />
          </clipPath>
        ))}
        {/* Pin gradient */}
        <linearGradient id="pin-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1d1d1d" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        {/* Animated chip border gradient */}
        <linearGradient id="chip-border-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff5c3c" stopOpacity="0" />
          <stop offset="40%" stopColor="#eb664b" stopOpacity="1" />
          <stop offset="60%" stopColor="#eb664b" stopOpacity="1" />
          <stop offset="100%" stopColor="#ff5c3c" stopOpacity="0" />
        </linearGradient>
        {/* Chip perimeter path for animateMotion */}
        <path
          id="chip-perim"
          d={`M ${L + 4} ${T} H ${R - 4} Q ${R} ${T} ${R} ${T + 4} V ${B - 4} Q ${R} ${B} ${R - 4} ${B} H ${L + 4} Q ${L} ${B} ${L} ${B - 4} V ${T + 4} Q ${L} ${T} ${L + 4} ${T}`}
        />
        {/* Mask for chip border pulse */}
        <mask id="chip-border-mask">
          <use href="#chip-perim" stroke="white" strokeWidth="1.5" fill="none" />
        </mask>
      </defs>

      {/* Trace lines */}
      <g fill="none" stroke="#c8c8d4" strokeWidth="0.6" opacity="0.7">
        {TRACES.map((tr, i) => (
          <path key={i} d={tr.d} />
        ))}
      </g>

      {/* Animated light pulses — one per trace */}
      {TRACES.map((tr, i) => (
        <g key={i} mask={`url(#mask${i})`}>
          <rect x="-18" y="-2" width="36" height="3" rx="1" fill={`url(#${tr.grad})`}>
            <animateMotion dur={tr.dur} repeatCount="indefinite" begin={tr.begin} calcMode="linear" rotate="auto">
              <mpath href={`#tp${i}`} />
            </animateMotion>
          </rect>
        </g>
      ))}

      {/* Chip body */}
      <rect
        x={L}
        y={T}
        width={CW}
        height={CH}
        rx="4"
        fill="url(#chip-body-filter)"
        stroke="#636376"
        strokeWidth="2"
        filter="url(#chip-shadow)"
      />

      {/* Inner die border */}
      <rect
        x={L + 6}
        y={T + 6}
        width={CW - 12}
        height={CH - 12}
        rx="2"
        fill="none"
        stroke="#2a2a32"
        strokeWidth="0.5"
        opacity="0.5"
      />

      {/* Inner circuit dots */}
      {[0, 1, 2, 3].map((gc) =>
        [0, 1, 2].map((gr) => {
          const dx = L + 12 + gc * ((CW - 24) / 3);
          const dy = T + 12 + gr * ((CH - 24) / 2);
          const active = (gc + gr) % 2 === 0;
          return (
            <circle
              key={`${gc}-${gr}`}
              cx={dx}
              cy={dy}
              r={active ? 1.2 : 0.7}
              fill={active ? '#4f4f66' : '#252530'}
              opacity={active ? 0.9 : 0.5}
            >
              {active && (
                <animate
                  attributeName="opacity"
                  values="0.8;0.3;0.8"
                  dur={`${2 + gc * 0.4 + gr * 0.3}s`}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
                />
              )}
            </circle>
          );
        }),
      )}

      {/* Animated chip border pulse */}
      <g mask="url(#chip-border-mask)">
        <rect x="-24" y="-4" width="48" height="8" rx="3" fill="url(#chip-border-grad)">
          <animateMotion dur="6s" repeatCount="indefinite" calcMode="linear" rotate="auto">
            <mpath href="#chip-perim" />
          </animateMotion>
        </rect>
      </g>

      {/* Pin stubs — top (4) */}
      {[L + 16, L + 30, R - 30, R - 16].map((x, i) => (
        <rect key={`pt${i}`} x={x - 2} y={T - PIN} width={4} height={PIN} rx="1" fill="url(#pin-grad)" opacity="0.9" />
      ))}
      {/* Pin stubs — bottom (4) */}
      {[L + 16, L + 30, R - 30, R - 16].map((x, i) => (
        <rect key={`pb${i}`} x={x - 2} y={B} width={4} height={PIN} rx="1" fill="url(#pin-grad)" opacity="0.9" />
      ))}
      {/* Pin stubs — left (4) */}
      {[T + 12, T + 26, B - 26, B - 12].map((y, i) => (
        <rect key={`pl${i}`} x={L - PIN} y={y - 2} width={PIN} height={4} rx="1" fill="url(#pin-grad)" opacity="0.9" />
      ))}
      {/* Pin stubs — right (4) */}
      {[T + 12, T + 26, B - 26, B - 12].map((y, i) => (
        <rect key={`pr${i}`} x={R} y={y - 2} width={PIN} height={4} rx="1" fill="url(#pin-grad)" opacity="0.9" />
      ))}

      {/* Icon trace lines */}
      <g fill="none" stroke="#c8c8d4" strokeWidth="0.5" opacity="0.25">
        {ICON_POSITIONS.map((ip) => (
          <path key={`il${ip.idx}`} d={ip.traceD} />
        ))}
      </g>

      {/* Icon trace animated pulses */}
      {ICON_POSITIONS.map((ip) => (
        <g key={`ia${ip.idx}`} mask={`url(#im${ip.idx})`}>
          <rect x="-14" y="-1.5" width="28" height="2.5" rx="1" fill={`url(#ig${ip.idx % ICON_GRAD_COLORS.length})`}>
            <animateMotion
              dur={`${(2.4 + ((ip.idx * 0.23) % 1.4)) * 1.2}s`}
              repeatCount="indefinite"
              begin={`${(ip.idx * 0.31) % 2}s`}
              calcMode="linear"
              rotate="auto"
            >
              <mpath href={`#itp${ip.idx}`} />
            </animateMotion>
          </rect>
        </g>
      ))}

      {/* Icon badges */}
      {ICON_POSITIONS.map((ip) => (
        <g key={`ib${ip.idx}`}>
          <circle
            cx={ip.ix}
            cy={ip.iy}
            r={ICON_R}
            fill="white"
            stroke="#d0d0dc"
            strokeWidth="0.8"
            filter="url(#chip-shadow)"
            opacity="0.95"
          />
          <image
            href={ip.path}
            x={ip.ix - ICON_R + 4}
            y={ip.iy - ICON_R + 4}
            width={(ICON_R - 4) * 2}
            height={(ICON_R - 4) * 2}
            clipPath={`url(#icp${ip.idx})`}
            preserveAspectRatio="xMidYMid meet"
          />
        </g>
      ))}

      {/* Chip label */}
      <image
        id="chip-label"
        href="/images/header/small-logo-white.svg"
        x={CX - 18}
        y={CY - 5}
        width="36"
        height="10"
        preserveAspectRatio="xMidYMid meet"
        opacity="0.85"
      />
    </svg>
  );
};

export default CpuArchitecture;
